import { Inbox, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/**
 * Dark-themed empty / null-state card. Rolls up the "none found",
 * "nothing yet", and "filtered to zero" patterns into one consistent
 * card with an icon, a short headline, and optional body / CTA slots.
 * Use the `tint` prop to colour the icon halo against the surrounding
 * page's accent (`sky` for Workfront pages, `violet` for KPI / Value,
 * `emerald` for Planning, `slate` as the neutral default).
 */
interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  tint?: 'slate' | 'sky' | 'violet' | 'emerald' | 'amber';
  className?: string;
}

const TINTS: Record<NonNullable<EmptyStateProps['tint']>, { bg: string; fg: string }> = {
  slate: { bg: 'bg-foreground/[0.04]', fg: 'text-foreground/50' },
  sky: { bg: 'bg-sky-500/15', fg: 'text-sky-700 dark:text-sky-300' },
  violet: { bg: 'bg-violet-500/15', fg: 'text-violet-700 dark:text-violet-300' },
  emerald: { bg: 'bg-emerald-500/15', fg: 'text-emerald-700 dark:text-emerald-300' },
  amber: { bg: 'bg-amber-500/15', fg: 'text-amber-700 dark:text-amber-300' },
};

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  tint = 'slate',
  className,
}: EmptyStateProps): JSX.Element {
  const t = TINTS[tint];
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.02] p-10 text-center',
        className,
      )}
    >
      <div className={cn('flex h-12 w-12 items-center justify-center rounded-full', t.bg)}>
        <Icon className={cn('h-6 w-6', t.fg)} />
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        {description ? (
          <div className="max-w-md text-xs leading-relaxed text-foreground/50">{description}</div>
        ) : null}
      </div>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
